import {inject, injectable} from 'inversify';
import {action, computed, makeObservable, observable, reaction} from 'mobx';

import type {
  AsyncBusyManager,
  Destructible,
  WithReactionDispose,
} from '../../../../utils/store';
import type {Nullable, StringKeysOnly} from '../../../../utils/utility-types';

import {not} from '../../../../utils/conditionalUtils';
import {ReactionDisposeManager} from '../../../../utils/store';
import {DataFilterState} from '../../models/DataFilterState';
import {DataFilterDatesManagerStore} from '../DataFilterDatesManager/DataFilterDatesManager.store';
import {DataFilterFieldsManagerStore} from '../DataFilterFieldsManager/DataFilterFieldsManager.store';
import {DataFilterLabelsStore} from '../DataFilterLabels/DataFilterLabels.store';
import {DataFilterSearchManagerStore} from '../DataFilterSearchManager/DataFilterSearchManager.store';
import {DataFilterStorageManagerStore} from '../DataFilterStorageManager/DataFilterStorageManager.store';
import {DATA_FILTER_STORE_COMMON_ID} from './utils';

@injectable()
export class DataFilterStore<Data = any>
  implements Destructible, WithReactionDispose
{
  busyLoading: Nullable<AsyncBusyManager<any>> = null;
  disposer = new ReactionDisposeManager();
  id = DATA_FILTER_STORE_COMMON_ID;
  shouldShowTotal = true;
  total = 0;
  private _state = new DataFilterState();
  private onStateChangeCb: Nullable<DataFilterStore.StateChangeCallback> = null;
  private shouldUseExternalSearch = false;
  private shouldUseExternalTotal = false;

  constructor(
    @inject(DataFilterDatesManagerStore)
    readonly datesManager: DataFilterDatesManagerStore<Data>,
    @inject(DataFilterFieldsManagerStore)
    readonly fieldsManager: DataFilterFieldsManagerStore,
    @inject(DataFilterLabelsStore) readonly labels: DataFilterLabelsStore,
    @inject(DataFilterSearchManagerStore)
    readonly searchManager: DataFilterSearchManagerStore<Data>,
    @inject(DataFilterStorageManagerStore)
    readonly storageManager: DataFilterStorageManagerStore,
  ) {
    makeObservable<DataFilterStore<Data>, '_state'>(this, {
      _state: observable,
      id: observable,
      init: action.bound,
      setState: action.bound,
      setTotal: action.bound,
      state: computed,
      total: observable,
    });
  }

  init(config: DataFilterStore.Config<Data>) {
    this.id = config.id;
    this.shouldShowTotal = config.shouldShowTotal ?? true;
    this.shouldUseExternalTotal = config.shouldUseExternalTotal ?? false;
    this.shouldUseExternalSearch = config.shouldUseExternalSearch ?? false;

    if (config.busyManager) {
      this.busyLoading = config.busyManager;
    }

    if (config.onStateChange) {
      this.onStateChangeCb = config.onStateChange;
    }

    this.storageManager.init(config.id);
    this.searchManager.init(config.search, this);
    this.datesManager.init(config.datesFilter, this);
    this.fieldsManager.init(config.fields, this);

    const state = this.storageManager.read();

    if (not(state.isClear)) {
      this.setState(state);
    }

    this.setupReactions();
  }

  destruct() {
    this.disposer.dispose();
  }

  get isLoading() {
    return Boolean(this.busyLoading?.anyReason);
  }

  get state(): DataFilterStore.State {
    return this._state;
  }

  filterData(data: Data[], state: DataFilterStore.State) {
    const bySearch = this.shouldUseExternalSearch
      ? data
      : this.searchManager.filterData(data, state);

    const byDates = this.datesManager.filterData(bySearch, state);
    const byFields = this.fieldsManager.filterData(byDates, state);

    if (!this.shouldUseExternalTotal) {
      const total = byFields.length;

      this.setTotal(total);
    }

    return byFields;
  }

  setState(value: DataFilterStore.State) {
    this._state = value;
  }

  setTotal(total: number) {
    this.total = total;
  }

  private setupReactions() {
    this.disposer.watch(
      'stateChange',
      reaction(
        () => this._state,
        state => this.stateChangeReaction(state),
      ),
    );
  }

  private stateChangeReaction(state: DataFilterState) {
    if (state) {
      this.storageManager.write(state);
      this.onStateChangeCb?.(state);
    }
  }
}

export namespace DataFilterStore {
  export type Config<Data> = {
    busyManager?: AsyncBusyManager<any>;
    datesFilter: DatesConfig<Data>;
    fields: Nullable<FieldsConfig<Data>>;
    id: string;
    onStateChange?: StateChangeCallback;
    search: SearchConfig<Data>;
    shouldShowTotal?: boolean;
    shouldUseExternalSearch?: boolean;
    shouldUseExternalTotal?: boolean;
  };
  export type DatesConfig<Data> = {
    endKey: Nullable<StringKeysOnly<Data>>;
    startKey: Nullable<StringKeysOnly<Data>>;
  };
  export type FieldItemConfig<Data> = {
    key: StringKeysOnly<Data>;
    label: string;
    placeholder: string;
  };
  export type FieldsConfig<Data> = {
    dataGetter: () => Data[];
    fields: FieldItemConfig<Data>[];
  };

  export type SearchConfig<Data> = {
    keysToSearch: StringKeysOnly<Data>[];
  };

  export type State = DataFilterState;

  export type StateChangeCallback = (state: DataFilterState) => void;

  export type StateSetter = (state: DataFilterState) => void;
}
